/**
 * Arbitration in the console (spec section 6): who yielded, to whom and why, wherever waiting
 * work appears, with "Start now". On the pipeline board, a quiet note of what waits for its
 * landing or deploy.
 */
import { Hourglass } from '@orgus/ui/icons';
import { useState } from 'react';
import { YIELD_WORK_LABEL } from '@orgus/protocol';
import type { Id, ProjectBusy, YieldHold, YieldWorkKind } from '@orgus/protocol';
import { Button, ErrorState, Popover, PopoverContent, PopoverTrigger, RelativeTime, Tooltip, cn, formatClock, formatInt, plural, toUiError } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { startHoldNow, useProjectProtocol } from './api.ts';
import { holdOf, holdText, holdsWaitingFor, reportFor } from './model.ts';
import type { WorkRef } from './model.ts';

function useProjectName(): (id: Id) => string | undefined {
  const projects = useOrgus((s) => s.projects);
  return (id) => projects[id]?.name;
}

/** The waiting hold of one piece of work, if it waits for a landing or deploy. */
export function useHold(kind: YieldWorkKind, work: WorkRef | null | undefined): YieldHold | undefined {
  const { state } = useProjectProtocol();
  return work ? holdOf(state, kind, work) : undefined;
}

function useStartNow(hold: YieldHold) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await startHoldNow(hold.id);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, start };
}

/** "Waits for Qawl-Agents: landing 4 submissions, since 8:41 PM" and Start now, on one line. */
export function HoldNote({ hold, className }: { hold: YieldHold; className?: string }) {
  const name = useProjectName();
  const { busy, error, start } = useStartNow(hold);
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex min-w-0 items-center gap-2 text-caption text-fg-muted">
        <Hourglass className="size-3 shrink-0 text-fg-subtle" aria-hidden />
        <Tooltip content={`Starts by itself at ${formatClock(hold.until)} if the ${hold.for.kind} has not ended`}>
          <span tabIndex={0} className="min-w-0 truncate" data-meta>
            {holdText(hold, name)}
          </span>
        </Tooltip>
        {hold.state === 'waiting' && (
          <Button size="sm" variant="ghost" className="ml-auto shrink-0" loading={busy} onClick={() => void start()}>
            Start now
          </Button>
        )}
      </div>
      {error && <ErrorState variant="inline" error={error} />}
    </div>
  );
}

function HoldRow({ hold }: { hold: YieldHold }) {
  const name = useProjectName();
  const { busy, error, start } = useStartNow(hold);
  return (
    <li className="flex flex-col gap-1 px-3 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-ui text-fg">{hold.label}</span>
          <span className="flex min-w-0 gap-2 text-caption text-fg-subtle">
            <span className="shrink-0">{YIELD_WORK_LABEL[hold.kind]}</span>
            <span className="min-w-0 truncate">{hold.projectId ? (name(hold.projectId) ?? 'Unknown project') : 'Orgus'}</span>
            <span className="shrink-0">
              <RelativeTime ts={hold.since} prefix="Waits" />
            </span>
          </span>
        </span>
        <Button size="sm" variant="ghost" loading={busy} onClick={() => void start()}>
          Start now
        </Button>
      </div>
      {error && <ErrorState variant="inline" error={error} />}
    </li>
  );
}

/** One quiet line under the board: what yields to this landing, with the list a click away. */
export function WaitingForBusy({ projectId, machineId, className }: { projectId: Id; machineId: Id; className?: string }) {
  const { state } = useProjectProtocol();
  const report = reportFor(state, projectId, machineId);
  const busy: ProjectBusy | undefined = report?.busy[0];
  if (!busy) return null;
  const holds = holdsWaitingFor(state, busy);
  if (!holds.length) return null;
  const what = busy.kind === 'deploy' ? 'this deploy' : 'this landing';
  const said = `${plural(holds.length, 'job')} ${holds.length === 1 ? 'waits' : 'wait'} for ${what}`;
  // One quiet control with its count: the sentence is its tooltip and accessible name.
  return (
    <div className={cn('flex items-center', className)}>
      <Popover>
        <Tooltip content={said}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost" icon={<Hourglass />} className="text-fg-muted" aria-label={`${said}: show them`}>
              Waiting work <span className="text-fg-subtle num">{formatInt(holds.length)}</span>
            </Button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent align="start" className="w-(--popover-max-width) p-1" aria-label="Work waiting for this landing">
          <p className="px-3 pb-1 pt-2 text-caption text-fg-subtle">Starts when the {busy.kind} ends, after 30m at most</p>
          <ul className="flex flex-col divide-y divide-border-subtle">
            {holds.map((h) => (
              <HoldRow key={h.id} hold={h} />
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
